import { Fragment, type ReactNode } from 'react';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';

/**
 * Markdown renderer for Orbi assistant responses.
 *
 * Supports what the model actually emits:
 *   # / ## / ### headings, ordered + unordered lists, **bold**, *italic*,
 *   `inline code`, ```fenced code blocks```, > blockquotes, [text](url) links.
 *
 * Special-cases a trailing "## References" or "## Sources" section — pulls it
 * out and renders it as a distinct card at the bottom so citations feel like
 * first-class evidence rather than an afterthought paragraph.
 */
export default function MarkdownContent({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  const normalized = content.replace(/\r\n/g, '\n');

  // Pull out any trailing References / Sources / Citations section so we can
  // render it as a card. Match "## References" at start of line, everything
  // to end of string.
  const refSplit = normalized.match(
    /^(##?\s*(?:References?|Sources?|Citations?))\s*\n([\s\S]+)$/m,
  );
  let body = normalized;
  let referencesRaw: string | null = null;
  if (refSplit) {
    const headingIndex = normalized.indexOf(refSplit[0]);
    body = normalized.slice(0, headingIndex).trimEnd();
    referencesRaw = refSplit[2].trim();
  }

  return (
    <div className={cn('text-sm leading-6 text-foreground', className)}>
      {renderMarkdownBlocks(body)}
      {referencesRaw && <ReferencesCard raw={referencesRaw} />}
    </div>
  );
}

function ReferencesCard({ raw }: { raw: string }) {
  // Each bullet becomes one reference row.
  const items = raw
    .split(/\n+/)
    .map((line) => line.trim())
    .filter((line) => line && /^[-*•]|\d+[.)]/.test(line))
    .map((line) => line.replace(/^[-*•]\s*|^\d+[.)]\s*/, ''));

  const nothingUsable = items.length === 0;

  return (
    <div className="mt-6 rounded-2xl border border-border bg-muted/40 p-4">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <BookOpenIcon className="h-3.5 w-3.5" />
        References
      </div>
      {nothingUsable ? (
        <div className="text-sm leading-6 text-foreground">
          {renderMarkdownBlocks(raw)}
        </div>
      ) : (
        <ol className="space-y-2 pl-6 text-sm leading-6 text-foreground">
          {items.map((item, i) => (
            <li key={i} className="pl-1 marker:text-muted-foreground marker:text-xs">
              {renderInline(item)}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * Block-level markdown → React nodes.
 * Walks line-by-line so we can group lists/blockquotes/code fences.
 */
function renderMarkdownBlocks(source: string): ReactNode[] {
  const lines = source.split('\n');
  const nodes: ReactNode[] = [];

  let listBuf: { ordered: boolean; items: string[] } | null = null;
  let quoteBuf: string[] | null = null;
  let codeBuf: { lang: string; lines: string[] } | null = null;
  let paraBuf: string[] | null = null;

  const flushList = () => {
    if (!listBuf) return;
    const key = `list-${nodes.length}`;
    if (listBuf.ordered) {
      nodes.push(
        <ol key={key} className="my-3 list-decimal space-y-1.5 pl-6">
          {listBuf.items.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ol>,
      );
    } else {
      nodes.push(
        <ul key={key} className="my-3 list-disc space-y-1.5 pl-6">
          {listBuf.items.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>,
      );
    }
    listBuf = null;
  };

  const flushQuote = () => {
    if (!quoteBuf) return;
    const key = `quote-${nodes.length}`;
    nodes.push(
      <blockquote
        key={key}
        className="my-4 border-l-4 border-primary/60 bg-muted/40 py-2 pl-4 pr-3 text-foreground/90 italic"
      >
        {quoteBuf.map((line, i) => (
          <p key={i} className={i > 0 ? 'mt-1' : ''}>
            {renderInline(line)}
          </p>
        ))}
      </blockquote>,
    );
    quoteBuf = null;
  };

  const flushPara = () => {
    if (!paraBuf) return;
    const key = `para-${nodes.length}`;
    nodes.push(
      <p key={key} className="my-3 first:mt-0 last:mb-0">
        {renderInline(paraBuf.join(' '))}
      </p>,
    );
    paraBuf = null;
  };

  const flushAllInline = () => {
    flushList();
    flushQuote();
    flushPara();
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];

    // Fenced code block start / end
    const fence = raw.match(/^\s*```(.*)$/);
    if (fence) {
      if (codeBuf) {
        // Close
        const key = `code-${nodes.length}`;
        nodes.push(
          <pre
            key={key}
            className="my-3 overflow-x-auto rounded-xl border border-border bg-muted/60 p-3 font-mono text-[0.8125rem] leading-5 text-foreground"
          >
            {codeBuf.lang && (
              <span className="mb-2 block text-[10px] uppercase tracking-wide text-muted-foreground">
                {codeBuf.lang}
              </span>
            )}
            <code>{codeBuf.lines.join('\n')}</code>
          </pre>,
        );
        codeBuf = null;
      } else {
        flushAllInline();
        codeBuf = { lang: fence[1].trim(), lines: [] };
      }
      continue;
    }
    if (codeBuf) {
      codeBuf.lines.push(raw);
      continue;
    }

    const trimmed = raw.trim();

    if (!trimmed) {
      flushAllInline();
      continue;
    }

    // Blockquote
    const quoteMatch = trimmed.match(/^>\s?(.*)$/);
    if (quoteMatch) {
      flushList();
      flushPara();
      if (!quoteBuf) quoteBuf = [];
      quoteBuf.push(quoteMatch[1]);
      continue;
    }

    // Unordered list
    const bulletMatch = trimmed.match(/^[-*•]\s+(.*)$/);
    if (bulletMatch) {
      flushQuote();
      flushPara();
      if (!listBuf || listBuf.ordered) {
        flushList();
        listBuf = { ordered: false, items: [] };
      }
      listBuf.items.push(bulletMatch[1]);
      continue;
    }

    // Ordered list
    const orderedMatch = trimmed.match(/^\d+[.)]\s+(.*)$/);
    if (orderedMatch) {
      flushQuote();
      flushPara();
      if (!listBuf || !listBuf.ordered) {
        flushList();
        listBuf = { ordered: true, items: [] };
      }
      listBuf.items.push(orderedMatch[1]);
      continue;
    }

    flushList();
    flushQuote();

    // Heading
    const heading = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      flushPara();
      const level = heading[1].length;
      const text = heading[2];
      const cls =
        level === 1
          ? 'mt-6 first:mt-0 text-xl font-semibold text-foreground tracking-tight'
          : level === 2
            ? 'mt-5 first:mt-0 text-lg font-semibold text-foreground tracking-tight'
            : 'mt-4 first:mt-0 text-base font-semibold text-foreground tracking-tight';
      nodes.push(
        <p key={`h-${i}`} className={cls}>
          {renderInline(text)}
        </p>,
      );
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(trimmed)) {
      flushPara();
      nodes.push(<hr key={`hr-${i}`} className="my-4 border-border" />);
      continue;
    }

    // Paragraph — buffer lines together (soft wrap).
    if (!paraBuf) paraBuf = [];
    paraBuf.push(trimmed);
  }

  flushAllInline();
  return nodes;
}

function renderInline(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*)|(\*[^*]+\*)|(`[^`]+`)|(\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      parts.push(
        <code
          key={key++}
          className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-foreground"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        parts.push(
          <a
            key={key++}
            href={linkMatch[2]}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline underline-offset-2 hover:opacity-80"
          >
            {linkMatch[1]}
          </a>,
        );
      }
    } else if (token.startsWith('*')) {
      parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.map((p, i) => <Fragment key={i}>{p}</Fragment>);
}
